// Moving one entry of an ordered list to another place, for a list rearranged
// by hand with a grip: the cashier's "My items" (UC-17) and the receipt's
// sections in Settings → Printing (UC-12).

/** The list with the entry at `from` taken out and put back at `to`. */
export function moveTo<T>(list: readonly T[], from: number, to: number): T[] {
	const next = [...list];
	if (from < 0 || from >= next.length) return next;
	const [picked] = next.splice(from, 1);
	const at = Math.max(0, Math.min(to, next.length));
	next.splice(at, 0, picked as T);
	return next;
}

/**
 * Where a dragged entry belongs, given the pointer's height and the middles of
 * the other entries in order: after every middle the pointer has passed.
 */
export function dropIndex(pointerY: number, middles: readonly number[]): number {
	let index = 0;
	for (const middle of middles) {
		if (pointerY > middle) index++;
	}
	return index;
}

/** Where a dragged row belongs among the other rows on screen, by their middles. */
export function dropAmong(pointerY: number, others: readonly Element[]): number {
	const middles = others.map((row) => {
		const box = row.getBoundingClientRect();
		return box.top + box.height / 2;
	});
	return dropIndex(pointerY, middles);
}

// Within this many CSS pixels of the scrolling area's top or bottom edge, a
// drag scrolls it this far a move.
const EDGE = 40;
const STEP = 12;

/**
 * How far a drag scrolls an area seen on screen from `top` to `bottom`: up
 * near its top edge, down near its bottom edge, and not at all between.
 */
export function edgeScroll(pointerY: number, top: number, bottom: number): number {
	if (pointerY < top + EDGE) return -STEP;
	if (pointerY > bottom - EDGE) return STEP;
	return 0;
}

/**
 * How many places a key on a focused grip moves its row: one up or one down
 * for the arrow keys, which then no longer scroll the page, and none for
 * another key.
 */
export function arrowStep(event: KeyboardEvent): -1 | 0 | 1 {
	const step = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
	if (step !== 0) event.preventDefault();
	return step;
}

/**
 * One drag by a grip, followed on the window rather than by capturing the
 * pointer on the grip: the list reorders under the pointer, and a row moved
 * in the page would drop a capture held by its grip. `move` hears every
 * pointer move until the pointer is lifted or cancelled, then `end` once.
 */
export class GripDrag {
	private readonly move: (event: PointerEvent) => void;
	private readonly end: () => void;
	private readonly target: EventTarget;

	constructor(move: (event: PointerEvent) => void, end: () => void, target: EventTarget = window) {
		this.move = move;
		this.end = end;
		this.target = target;
	}

	/** Follows the drag a press on a grip starts; false for a mouse's other buttons. */
	start(event: PointerEvent): boolean {
		if (event.pointerType === 'mouse' && event.button !== 0) return false;
		event.preventDefault();
		this.target.addEventListener('pointermove', this.move as EventListener);
		this.target.addEventListener('pointerup', this.finish);
		this.target.addEventListener('pointercancel', this.finish);
		return true;
	}

	/** Stops following without ending the drag, for a screen that closes mid-drag. */
	stop(): void {
		this.target.removeEventListener('pointermove', this.move as EventListener);
		this.target.removeEventListener('pointerup', this.finish);
		this.target.removeEventListener('pointercancel', this.finish);
	}

	private readonly finish = (): void => {
		this.stop();
		this.end();
	};
}
